import { useEffect, useMemo, useState } from "react";
import { clsx } from "clsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ChevronDown, ChevronRight, CloudOff, Copy, Download, Eye, EyeOff, Folder, FolderArchive,
  FolderInput, FolderOpen, FolderPlus, History, Image as ImageIcon, Info, Layers, Paperclip, Pencil, PenLine, RefreshCw,
  Scale, Trash2, Upload, X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import { OdtEditorScreen } from "@/features/doc_editor/components/OdtEditorScreen";
import { useEditorAvailability } from "@/features/doc_editor/hooks";
import { MediaLightbox } from "@/features/chat/components/MediaLightbox";
import { imageSeries, type ImageSeries } from "@/features/doc_editor/imagePreview";
import { filesTabOpenAction, type FilesTabOpenAction } from "@/features/doc_editor/types";
import { useTaskPolling } from "@/features/power_projects/hooks";
import * as api from "../api";
import { filesKey, useLedFiles, useLedFilesMutation, useLedPreview } from "../hooks";
import type { LedFilesPayload, LedFolderEntry, LedFolderNode } from "../types";
import { ErrorLine, SideTag, errMsg, fmtDateTime } from "./common";

/**
 * Zakładka „Pliki" prowadzonego projektu — ten sam układ dwóch paneli, co zakładka
 * „Pliki" zwykłego projektu ZE (features/power_projects/components/FilesTab), ale po
 * przekazaniu to KOPIA, nie projekcja: bez kłódek, każdy folder przyjmuje pliki obu
 * stron; firma zarządza wyłącznie tym, co sama wgrała (`entry.can_manage`), a ELEKTIM
 * może schować folder albo plik przed firmą („ukryj dla podwykonawcy"). Co widz może,
 * mówi backend — komponent nie zgaduje po roli.
 *
 * Pliki w formatach, które zna Collabora (discovery serwera), otwierają się w edytorze w
 * przeglądarce (slot `led_file`): do EDYCJI, gdy widz plikiem zarządza i format da się
 * zapisać, inaczej do podglądu. Tryb rozstrzyga backend (UserCanWrite); front tylko
 * zapowiada go w podpowiedzi i w podglądzie „jak podwykonawca" wymusza sam podgląd.
 *
 * Zdjęcia (jpg/png…) nie idą do Collabory — otwierają się w podglądzie jak multimedia w GG
 * (MediaLightbox, strzałki po kolejnych zdjęciach folderu).
 *
 * Załączniki kafli z zakładki „Etapy" (`entry.tile_file_id`) są tu PROJEKCJĄ w folderze
 * kamienia — ten sam plik po obu stronach, bez kopiowania; opis/usunięcie robi się przy kaflu,
 * tu podgląd, pobranie i Collabora slotem `led_tile_file`.
 *
 * Dokumenty pozyskanych tytułów (`entry.title_document`) to też projekcja, u obu stron bez
 * „Dokopiuj": zarządza się nimi przy tytule, tu podgląd slotem `led_title_file`, pobranie
 * i „Ukryj dla podwykonawcy" (kopia od razu ukryta).
 */

type TransferSource =
  | { kind: "file"; filename: string; fromSlug: string; fileId: string }
  | { kind: "folder"; slug: string; label: string };
type TransferTarget = { operation: "copy" | "move"; source: TransferSource };

function fmtBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// Liście (jedyne foldery z plikami i uploadem) — kolejność drzewa.
function collectLeaves(nodes: LedFolderNode[], acc: LedFolderNode[] = []): LedFolderNode[] {
  for (const n of nodes) {
    if (n.children.length === 0) acc.push(n);
    else collectLeaves(n.children, acc);
  }
  return acc;
}

function findNode(nodes: LedFolderNode[], slug: string): LedFolderNode | null {
  for (const n of nodes) {
    if (n.slug === slug) return n;
    const hit = findNode(n.children, slug);
    if (hit) return hit;
  }
  return null;
}

const HIDDEN_HINT = "Ukryty dla podwykonawcy — widzi go tylko GK ELEKTIM";

// ── lewy panel: drzewo ─────────────────────────────────────────────────────────
function TreeNode({
  node, depth, selectedSlug, onSelect, onDownloadZip, zipBusy,
  canHide, onToggleHidden, hideBusy, onRenameFolder, onDeleteFolder,
}: {
  node: LedFolderNode; depth: number; selectedSlug: string; onSelect: (slug: string) => void;
  onDownloadZip: (slug: string) => void; zipBusy: boolean;
  canHide: boolean; onToggleHidden: (slug: string, hidden: boolean) => void; hideBusy: boolean;
  onRenameFolder: (node: LedFolderNode) => void; onDeleteFolder: (node: LedFolderNode) => void;
}) {
  const isContainer = node.children.length > 0;
  const [open, setOpen] = useState(true);
  const selected = node.slug === selectedSlug;
  const hidden = node.hidden_effective;

  return (
    <div>
      <div
        className={clsx(
          "group flex items-center py-1.5 pr-2 text-sm transition-colors",
          selected
            ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium"
            : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]",
          hidden && "opacity-70",
        )}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
      >
        <button
          type="button"
          onClick={() => (isContainer ? setOpen((o) => !o) : onSelect(node.slug))}
          className="flex-1 min-w-0 flex items-center gap-1.5 text-left"
        >
          {isContainer ? (
            open ? <ChevronDown size={13} className="shrink-0" /> : <ChevronRight size={13} className="shrink-0" />
          ) : (
            <span className="w-[13px] shrink-0" />
          )}
          {selected ? <FolderOpen size={14} className="shrink-0" /> : <Folder size={14} className="shrink-0" />}
          <span className="truncate flex-1" title={node.label}>{node.label}</span>
          {hidden && (
            <EyeOff
              size={12}
              className="shrink-0 text-amber-600"
              aria-label={node.hidden_from_contractor ? HIDDEN_HINT : `${HIDDEN_HINT} (dziedziczone z folderu nadrzędnego)`}
            />
          )}
          {node.file_count > 0 && (
            <span className="shrink-0 text-[10px] font-mono px-1 rounded bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]">
              {node.file_count}
            </span>
          )}
        </button>
        {node.is_custom && node.can_manage && (
          <>
            <button
              type="button"
              className="shrink-0 p-0.5 ml-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              title={`Zmień nazwę folderu „${node.label}"`}
              onClick={() => onRenameFolder(node)}
            >
              <Pencil size={13} />
            </button>
            <button
              type="button"
              className="shrink-0 p-0.5 ml-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              title={`Usuń folder „${node.label}"`}
              onClick={() => onDeleteFolder(node)}
            >
              <Trash2 size={13} />
            </button>
          </>
        )}
        {canHide && (
          <button
            type="button"
            className={clsx(
              "shrink-0 p-0.5 ml-1.5 transition-opacity disabled:opacity-40",
              node.hidden_from_contractor
                ? "text-amber-600 hover:text-[var(--color-text-primary)] opacity-100"
                : "text-[var(--color-text-muted)] hover:text-amber-600 opacity-0 group-hover:opacity-100 focus:opacity-100",
            )}
            title={
              node.hidden_from_contractor
                ? `Pokaż folder „${node.label}" podwykonawcy`
                : `Ukryj folder „${node.label}" dla podwykonawcy (obejmuje podfoldery i pliki)`
            }
            disabled={hideBusy}
            onClick={() => onToggleHidden(node.slug, !node.hidden_from_contractor)}
          >
            {node.hidden_from_contractor ? <Eye size={13} /> : <EyeOff size={13} />}
          </button>
        )}
        {node.file_count > 0 && (
          <button
            type="button"
            className="shrink-0 p-0.5 ml-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-40 transition-opacity"
            title={`Pobierz „${node.label}" jako ZIP`}
            disabled={zipBusy}
            onClick={() => onDownloadZip(node.slug)}
          >
            <Download size={13} />
          </button>
        )}
      </div>
      {isContainer && open && node.children.map((c) => (
        <TreeNode
          key={c.slug}
          node={c}
          depth={depth + 1}
          selectedSlug={selectedSlug}
          onSelect={onSelect}
          onDownloadZip={onDownloadZip}
          zipBusy={zipBusy}
          canHide={canHide}
          onToggleHidden={onToggleHidden}
          hideBusy={hideBusy}
          onRenameFolder={onRenameFolder}
          onDeleteFolder={onDeleteFolder}
        />
      ))}
    </div>
  );
}

// ── prawy panel: wiersz pliku ──────────────────────────────────────────────────
function EntryRow({
  entry, contractorName, saving, openAction, onOpen, onSaveDescription, onSaveFilename, onDelete, onToggleHidden, onCopy, onMove, onPrehide,
}: {
  entry: LedFolderEntry;
  contractorName: string;
  saving: boolean;
  /** Jak otworzyć: „edit" = Collabora z prawem zapisu dla tego widza, „view" = podgląd w
   *  Collaborze, „image" = podgląd zdjęcia jak w GG. */
  openAction?: FilesTabOpenAction;
  onOpen?: () => void;
  onSaveDescription: (description: string, close: () => void) => void;
  onSaveFilename: (filename: string, close: () => void) => void;
  onDelete: () => void;
  /** Tylko ELEKTIM — „ukryj dla podwykonawcy" na pojedynczym pliku. */
  onToggleHidden?: (hidden: boolean) => void;
  onCopy: () => void;
  /** Przenoszenie — tylko pliki, którymi ten widz zarządza. */
  onMove?: () => void;
  /** Plik projektu ELEKTIM jeszcze nieskopiowany: „Ukryj dla podwykonawcy" = kopia od razu ukryta. */
  onPrehide?: () => void;
}) {
  const manageable = entry.can_manage;
  const pending = !!entry.pending_copy;
  const fromTile = entry.tile_file_id != null;
  const fromTitle = !!entry.title_document;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.description);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(entry.filename);
  const save = () => onSaveDescription(draft.trim(), () => setEditing(false));
  const saveName = () => {
    const next = nameDraft.trim();
    if (next) onSaveFilename(next, () => setEditingName(false));
  };

  return (
    <tr
      className={clsx(
        "border-b border-[var(--color-border-muted)] last:border-0 hover:bg-[var(--color-bg-elevated)] transition-colors",
        (entry.hidden_from_contractor || pending) && "opacity-70",
      )}
      data-testid={`entry-${entry.id}`}
    >
      <td className="py-3 pl-4 pr-2">
        <Paperclip size={14} className="text-[var(--color-text-muted)]" />
      </td>
      <td className="py-3 pr-4 w-full max-w-0">
        {editingName ? (
          <div className="flex items-center gap-1.5">
            <input
              className="input-base text-xs py-1"
              value={nameDraft}
              autoFocus
              placeholder="Nazwa pliku"
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveName();
                if (e.key === "Escape") setEditingName(false);
              }}
            />
            <Button size="sm" loading={saving} onClick={saveName}>Zapisz</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>Anuluj</Button>
          </div>
        ) : (
          <p className="text-sm font-medium text-[var(--color-text-primary)] min-w-0 flex items-center gap-1.5" title={entry.filename}>
            {onOpen ? (
              <button
                type="button"
                className="min-w-0 inline-flex items-center gap-1.5 text-left hover:text-[var(--color-primary)] hover:underline"
                title={
                  openAction === "image" ? "Otwórz podgląd zdjęcia"
                    : openAction === "edit" ? "Otwórz do edycji w przeglądarce" : "Otwórz podgląd w przeglądarce"
                }
                onClick={onOpen}
              >
                {openAction === "image"
                  ? <ImageIcon size={12} className="shrink-0 text-[var(--color-text-muted)]" />
                  : openAction === "edit"
                    ? <PenLine size={12} className="shrink-0 text-[var(--color-text-muted)]" />
                    : <Eye size={12} className="shrink-0 text-[var(--color-text-muted)]" />}
                <span className="truncate">{entry.filename}</span>
              </button>
            ) : (
              <span className="truncate">{entry.filename}</span>
            )}
            {manageable && (
              <button
                type="button"
                className="p-0.5 shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                title="Zmień nazwę pliku"
                onClick={() => { setNameDraft(entry.filename); setEditingName(true); }}
              >
                <Pencil size={12} />
              </button>
            )}
            {entry.copied_at_handover && (
              <History
                size={12}
                className="shrink-0 text-[var(--color-text-muted)]"
                aria-label="Kopia z chwili przekazania projektu"
              />
            )}
            {entry.hidden_from_contractor && (
              <EyeOff size={12} className="shrink-0 text-amber-600" aria-label={HIDDEN_HINT} />
            )}
            {pending && (
              <span
                className="inline-flex shrink-0 items-center gap-1 border border-amber-500 px-1 py-px font-mono text-[10px] font-normal uppercase tracking-wider text-amber-700 dark:text-amber-400"
                title={
                  entry.pending_confidential
                    ? "Plik projektu ELEKTIM, jeszcze nieskopiowany do zakładki podwykonawcy. W projekcie jest poufny — po dokopiowaniu będzie od razu ukryty przed firmą."
                    : "Plik projektu ELEKTIM, jeszcze nieskopiowany do zakładki podwykonawcy — firma go nie widzi. Skopiuje go „Dokopiuj nowe pliki projektu”; wcześniej możesz go ukryć dla podwykonawcy."
                }
                data-testid="pending-badge"
              >
                <CloudOff size={10} /> nie skopiowano do podwykonawcy
              </span>
            )}
            {fromTile && (
              <span
                className="inline-flex shrink-0 items-center gap-1 border border-[var(--color-border)] px-1 py-px font-mono text-[10px] font-normal uppercase tracking-wider text-[var(--color-text-muted)]"
                title={`Załącznik kafla „${entry.tile_milestone_label ?? ""}” z zakładki „Etapy” — ten sam plik widzą obie strony; opis i usunięcie przy kaflu.`}
                data-testid="tile-badge"
              >
                <Layers size={10} /> z Etapów
              </span>
            )}
            {fromTitle && (
              <span
                className="inline-flex shrink-0 items-center gap-1 border border-[var(--color-border)] px-1 py-px font-mono text-[10px] font-normal uppercase tracking-wider text-[var(--color-text-muted)]"
                title="Dokument pozyskanego tytułu prawnego — ten sam plik jest przy właścicielu na karcie działki i widzą go obie strony."
                data-testid="title-badge"
              >
                <Scale size={10} /> tytuł prawny
              </span>
            )}
          </p>
        )}
        {editing ? (
          <div className="flex items-center gap-1.5 mt-1">
            <input
              className="input-base text-xs py-1"
              value={draft}
              autoFocus
              placeholder="Opis pliku"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditing(false);
              }}
            />
            <Button size="sm" loading={saving} onClick={save}>Zapisz</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Anuluj</Button>
          </div>
        ) : (
          <p className="text-xs text-[var(--color-text-secondary)] mt-0.5 min-w-0 flex items-center gap-1.5">
            {entry.description ? (
              <span className="truncate" title={entry.description}>{entry.description}</span>
            ) : (
              manageable && <span className="text-[var(--color-text-muted)]">bez opisu</span>
            )}
            {manageable && (
              <button
                type="button"
                className="p-0.5 shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                title="Edytuj opis"
                onClick={() => { setDraft(entry.description); setEditing(true); }}
              >
                <Pencil size={12} />
              </button>
            )}
          </p>
        )}
        {/* Kto wgrał: strona + inicjały, a przy kopii z przekazania — skąd plik pochodził
            w projekcie ELEKTIM (trasa, wniosek, decyzja…). */}
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[var(--color-text-muted)]">
          <SideTag side={entry.side} contractorName={contractorName} />
          <span title={entry.uploaded_by_full_name ?? undefined}>{entry.uploaded_by_label}</span>
          {entry.copied_at_handover && (
            <span className="inline-flex items-center gap-1" title={entry.origin_label || undefined}>
              <History size={11} /> kopia z przekazania{entry.origin_label && entry.origin_label !== entry.description ? ` · ${entry.origin_label}` : ""}
            </span>
          )}
          {entry.scan_pending && (
            <span className="inline-flex items-center gap-1 text-amber-600">
              <AlertTriangle size={11} /> czeka na skan antywirusowy
            </span>
          )}
        </p>
      </td>
      <td className="py-3 pr-4 text-xs font-mono text-[var(--color-text-muted)] whitespace-nowrap">
        {fmtBytes(entry.size_bytes)}
      </td>
      <td className="py-3 pr-4 text-xs text-[var(--color-text-muted)] whitespace-nowrap">
        {fmtDateTime(entry.created_at)}
      </td>
      <td className="py-3 pr-3">
        <div className="flex items-center gap-1 justify-end">
          {onPrehide && (
            <button
              type="button"
              className="p-1.5 text-[var(--color-text-muted)] hover:text-amber-600 transition-colors"
              title="Ukryj dla podwykonawcy — plik zostanie skopiowany do zakładki od razu jako ukryty"
              onClick={onPrehide}
            >
              <EyeOff size={15} />
            </button>
          )}
          {!pending && !fromTitle && onToggleHidden && (
            <button
              type="button"
              className={clsx(
                "p-1.5 transition-colors",
                entry.hidden_from_contractor
                  ? "text-amber-600 hover:text-[var(--color-text-primary)]"
                  : "text-[var(--color-text-muted)] hover:text-amber-600",
              )}
              title={entry.hidden_from_contractor ? "Pokaż plik podwykonawcy" : "Ukryj plik dla podwykonawcy"}
              onClick={() => onToggleHidden(!entry.hidden_from_contractor)}
            >
              {entry.hidden_from_contractor ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
          )}
          {!pending && !fromTile && !fromTitle && (
            <button
              type="button"
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
              title="Kopiuj do folderu…"
              onClick={onCopy}
            >
              <Copy size={15} />
            </button>
          )}
          {onMove && (
            <button
              type="button"
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
              title="Przenieś do folderu…"
              onClick={onMove}
            >
              <FolderInput size={15} />
            </button>
          )}
          {entry.url ? (
            <a
              href={entry.url}
              download={entry.filename}
              target="_blank"
              rel="noreferrer"
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
              title="Pobierz"
            >
              <Download size={15} />
            </a>
          ) : (
            <span className="p-1.5 text-[var(--color-text-muted)] opacity-40" title="Plik jeszcze niedostępny">
              <Download size={15} />
            </span>
          )}
          {manageable && (
            <button
              type="button"
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
              title="Usuń plik"
              onClick={onDelete}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── formularz wgrywania (jeden plik = jedno żądanie, opis per plik) ────────────
function UploadForm({ leadershipId, folderSlug, onDone }: { leadershipId: string; folderSlug: string; onDone: () => void }) {
  const upload = useLedFilesMutation(leadershipId, (v: { file: File; description: string }) =>
    api.uploadLedFile(leadershipId, v.file, folderSlug, v.description));
  const [files, setFiles] = useState<File[]>([]);
  const [descriptions, setDescriptions] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFilesChange = (next: File[]) => {
    setFiles(next);
    setDescriptions((prev) => {
      const d = [...prev];
      while (d.length < next.length) d.push("");
      return d.slice(0, next.length);
    });
  };
  const reset = () => { setFiles([]); setDescriptions([]); setError(null); onDone(); };
  const submit = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      // Sekwencyjnie: każda odpowiedź to pełne drzewo, równoległe zapisy nadpisywałyby
      // sobie cache nawzajem (ostatni wygrany mógłby być „starszy").
      for (let i = 0; i < files.length; i++) {
        await upload.mutateAsync({ file: files[i], description: descriptions[i] ?? "" });
      }
      reset();
    } catch (e) {
      setError(errMsg(e, "Nie udało się przesłać niektórych plików."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card p-4 space-y-4 border-[var(--color-primary)]/30">
      <div>
        <label className="section-label block mb-1.5">Pliki *</label>
        <FileDropZone multiple files={files} onFilesChange={handleFilesChange} />
      </div>
      {files.length > 0 && (
        <div className="space-y-3">
          {files.map((file, i) => (
            <div key={`${file.name}-${i}`} className="flex items-start gap-3 p-2.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border-muted)]">
              <Paperclip size={13} className="text-[var(--color-text-muted)] mt-2 shrink-0" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <p className="text-xs font-medium text-[var(--color-text-primary)] truncate">{file.name}</p>
                <input
                  className="input-base text-xs py-1 w-full"
                  placeholder="Opis (opcjonalnie)"
                  value={descriptions[i] ?? ""}
                  onChange={(e) => setDescriptions((prev) => prev.map((d, j) => (j === i ? e.target.value : d)))}
                />
              </div>
              <button
                type="button"
                className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors shrink-0 mt-1"
                aria-label={`Usuń ${file.name} z listy`}
                onClick={() => handleFilesChange(files.filter((_, j) => j !== i))}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" disabled={files.length === 0} loading={uploading} onClick={submit}>
          Prześlij {files.length > 1 ? `(${files.length})` : ""}
        </Button>
        <Button size="sm" variant="ghost" onClick={reset}>Anuluj</Button>
      </div>
    </div>
  );
}

// ── modal „kopiuj / przenieś do folderu" ───────────────────────────────────────
function TransferModal({ leadershipId, target, folders, onClose }: {
  leadershipId: string; target: TransferTarget; folders: LedFolderNode[]; onClose: () => void;
}) {
  const transferM = useLedFilesMutation(leadershipId, (body: Parameters<typeof api.transferLedFiles>[1]) =>
    api.transferLedFiles(leadershipId, body));
  const [destSlug, setDestSlug] = useState("");
  const [error, setError] = useState("");
  const [doneCount, setDoneCount] = useState<number | null>(null);
  const { operation, source } = target;
  const excludeSlug = source.kind === "file" ? source.fromSlug : source.slug;
  const options = collectLeaves(folders)
    .filter((f) => f.slug !== excludeSlug)
    .map((f) => ({ value: f.slug, label: `${f.label} (${f.file_count})` }));
  const isCopy = operation === "copy";
  const sourceLabel = source.kind === "file" ? `plik „${source.filename}"` : `zawartość folderu „${source.label}"`;

  const submit = () => {
    setError("");
    transferM.mutate(
      {
        operation,
        target_slug: destSlug,
        file_id: source.kind === "file" ? source.fileId : undefined,
        source_slug: source.kind === "folder" ? source.slug : undefined,
      },
      {
        onSuccess: (tree) => setDoneCount(tree.transferred ?? 0),
        onError: (e) => setError(errMsg(e, isCopy ? "Nie udało się skopiować." : "Nie udało się przenieść.")),
      },
    );
  };

  return (
    <Modal open onClose={onClose} title={isCopy ? "Kopiuj do folderu" : "Przenieś do folderu"} size="sm">
      {doneCount != null ? (
        <div className="space-y-3">
          <p className="text-sm text-green-600 dark:text-green-400">
            {isCopy ? "Skopiowano" : "Przeniesiono"} {doneCount} {doneCount === 1 ? "plik" : doneCount < 5 ? "pliki" : "plików"}.
          </p>
          <Button className="w-full" onClick={onClose}>Zamknij</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {isCopy ? "Kopiujesz" : "Przenosisz"} {sourceLabel}.
            {isCopy
              ? " Kopia pliku ELEKTIM pozostaje plikiem ELEKTIM — firma nadal ma ją tylko do podglądu."
              : source.kind === "folder" ? " Przeniesione zostaną tylko pliki, którymi zarządzasz." : ""}
          </p>
          {options.length === 0 ? (
            <p className="text-xs text-amber-600">Brak dostępnych folderów docelowych.</p>
          ) : (
            <SearchableSelect
              options={options}
              value={destSlug}
              placeholder="— wybierz folder docelowy —"
              onChange={(e) => setDestSlug(e.target.value)}
            />
          )}
          {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}
          <div className="flex gap-2">
            <Button className="flex-1" disabled={!destSlug} loading={transferM.isPending} onClick={submit}>
              {isCopy ? <Copy size={13} className="mr-1.5" /> : <FolderInput size={13} className="mr-1.5" />}
              {isCopy ? "Kopiuj" : "Przenieś"}
            </Button>
            <Button variant="ghost" onClick={onClose}>Anuluj</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── stan kopii z przekazania + „Dokopiuj nowe pliki projektu" (tylko ELEKTIM) ──────
// Zakładka jest kopią z chwili przekazania i nie śledzi zmian w drzewie DLC_01. Co trafiło
// do projektu później (wynik geodety, dekretacja, plik kamienia, który dopiero teraz ma swój
// folder), firma zobaczy po dokopiowaniu — idempotentnym (dokłada tylko brakujące), więc
// przycisk można klikać bez obaw. Uruchamia koordynator / dyrektor / CEO (`can_resync`).
function SnapshotBar({ leadershipId, snapshot, canResync, pendingCount, pendingError }: {
  leadershipId: string; snapshot: NonNullable<LedFilesPayload["snapshot"]>; canResync: boolean; pendingCount: number;
  /** Lista nieskopiowanych niepełna — drzewo plików projektu nie dało się odczytać. */
  pendingError?: string;
}) {
  const qc = useQueryClient();
  const snapM = useLedFilesMutation(leadershipId, () => api.snapshotLedFiles(leadershipId));
  const [taskId, setTaskId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const task = useTaskPolling(taskId);
  const status = task.data?.status;
  const running = !!taskId && (!status || !["success", "failure", "cancelled"].includes(status));
  // Kopia skończona w tle — drzewo trzeba pobrać na nowo (odpowiedź POST była sprzed kopii).
  useEffect(() => {
    if (status === "success" || status === "failure") qc.invalidateQueries({ queryKey: filesKey(leadershipId) });
  }, [status, qc, leadershipId]);
  const copied = status === "success" ? Number((task.data?.result as { copied?: number } | null)?.copied ?? 0) : null;

  const run = () => {
    setError("");
    snapM.mutate(undefined, {
      onSuccess: (tree) => setTaskId(tree.task_id ?? null),
      onError: (e) => setError(errMsg(e, "Nie udało się zlecić kopii.")),
    });
  };
  const button = canResync && (
    <Button size="sm" variant="secondary" loading={snapM.isPending || running} disabled={running} onClick={run}>
      <RefreshCw size={13} className="mr-1.5" /> {snapshot.done ? "Dokopiuj nowe pliki projektu" : "Skopiuj teraz"}
    </Button>
  );
  const outcome = taskId && !running && (
    <span className={clsx("text-[12px]", status === "success" ? "text-emerald-700 dark:text-emerald-400" : "text-[var(--color-primary)]")}>
      {status === "success"
        ? copied ? `Dokopiowano ${copied} ${copied === 1 ? "plik" : copied < 5 ? "pliki" : "plików"}.` : "Nic nowego — zakładka ma już wszystkie pliki projektu."
        : task.data?.error || "Kopia nie powiodła się — zobacz zadanie w tle."}
    </span>
  );
  // Poprzednia próba (sprzed odświeżenia strony) padła albo wisi — bez tego pasek wyglądałby
  // jak stan wyjściowy „jeszcze nie skopiowano", a nie jak awaria do powtórzenia.
  const last = snapshot.last_task;
  const lastFailed = !taskId && last && (last.status === "failure" || last.status === "cancelled");
  const lastStuck = !taskId && last && (last.status === "pending" || last.status === "running");
  const previous = (lastFailed || lastStuck) && (
    <span className={clsx("basis-full text-[12px]", lastFailed ? "text-[var(--color-primary)]" : "text-[var(--color-text-secondary)]")} data-testid="snapshot-last-task">
      {lastFailed
        ? `Ostatnia próba kopii (${fmtDateTime(last.created_at)}) nie powiodła się${last.error ? `: ${last.error}` : "."} Uruchom ją ponownie.`
        : `Kopia zlecona ${fmtDateTime(last.created_at)} jeszcze trwa — odśwież zakładkę za chwilę.`}
    </span>
  );

  if (!snapshot.done) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border border-amber-400/60 bg-amber-50/70 px-3 py-2 text-[13px] dark:border-amber-700/70 dark:bg-amber-950/30">
        <AlertTriangle size={14} className="shrink-0 text-amber-700 dark:text-amber-400" />
        <span className="min-w-0 flex-1">
          {running
            ? "Kopiowanie plików projektu trwa w tle…"
            : "Pliki projektu z chwili przekazania nie zostały jeszcze skopiowane do tej zakładki (kopia idzie w tle zaraz po przekazaniu)."}
        </span>
        {outcome}
        {button}
        {previous}
        {pendingError && <span className="basis-full text-[12px] text-[var(--color-primary)]" data-testid="pending-error">{pendingError}</span>}
        {error && <span className="basis-full text-[12px] text-[var(--color-primary)]">{error}</span>}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-[12px] text-[var(--color-text-secondary)]">
      <History size={13} className="shrink-0 text-[var(--color-text-muted)]" />
      <span className="min-w-0 flex-1">
        Kopia plików projektu z {fmtDateTime(snapshot.at)} · {snapshot.copied_count} {snapshot.copied_count === 1 ? "plik" : "plików"} z przekazania.
        {running
          ? " Dokopiowanie trwa w tle…"
          : pendingCount
            ? ` ${pendingCount === 1 ? "1 plik projektu czeka" : `${pendingCount} plików projektu czeka`} na dokopiowanie — firma ich jeszcze nie widzi.`
            : " Firma widzi wszystkie pliki projektu (poza ukrytymi)."}
      </span>
      {outcome}
      {button}
      {previous}
      {pendingError && <span className="basis-full text-[var(--color-primary)]" data-testid="pending-error">{pendingError}</span>}
      {error && <span className="basis-full text-[var(--color-primary)]">{error}</span>}
    </div>
  );
}

// ── zakładka ───────────────────────────────────────────────────────────────────
export function FilesTab({ leadershipId }: { leadershipId: string }) {
  const preview = useLedPreview();
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useLedFiles(leadershipId);
  // Formaty, które Collabora edytuje / pokazuje — bez edytora na serwerze nic się nie otwiera.
  const { data: editorAvail } = useEditorAvailability();
  const editorExt = editorAvail?.available ? editorAvail.extensions ?? null : null;
  const [editorFile, setEditorFile] = useState<LedFolderEntry | null>(null);
  // Podgląd zdjęć jak w GG — seria obrazów wybranego folderu.
  const [lightbox, setLightbox] = useState<ImageSeries | null>(null);
  const patchM = useLedFilesMutation(leadershipId, (v: { id: string; description?: string; filename?: string }) =>
    api.patchLedFile(leadershipId, v.id, { description: v.description, filename: v.filename }));
  const removeM = useLedFilesMutation(leadershipId, (id: string) => api.removeLedFile(leadershipId, id));
  const hideFileM = useLedFilesMutation(leadershipId, (v: { id: string; hidden: boolean }) => api.setLedFileHidden(leadershipId, v.id, v.hidden));
  const hideFolderM = useLedFilesMutation(leadershipId, (v: { slug: string; hidden: boolean }) => api.setLedFolderHidden(leadershipId, v.slug, v.hidden));
  const createFolderM = useLedFilesMutation(leadershipId, (label: string) => api.createLedFolder(leadershipId, label));
  const renameFolderM = useLedFilesMutation(leadershipId, (v: { slug: string; label: string }) => api.renameLedFolder(leadershipId, v.slug, v.label));
  const deleteFolderM = useLedFilesMutation(leadershipId, (slug: string) => api.deleteLedFolder(leadershipId, slug));
  const prehideM = useLedFilesMutation(leadershipId, (v: { managed_file_id: string; folder_slug: string }) =>
    api.copyPendingLedFile(leadershipId, { ...v, hidden: true }));
  const zipM = useMutation({ mutationFn: (slug: string | null) => api.downloadLedFilesZip(leadershipId, slug ?? undefined, preview) });

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [transferTarget, setTransferTarget] = useState<TransferTarget | null>(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderLabel, setNewFolderLabel] = useState("");
  const [createError, setCreateError] = useState("");
  const [renamingFolder, setRenamingFolder] = useState<{ slug: string; label: string } | null>(null);
  const [renameError, setRenameError] = useState("");

  const folders = data?.folders ?? [];
  const internal = !!data?.internal;
  const canHide = !!data?.can_hide && !preview;
  const contractorName = data?.contractor_name ?? "firma";
  const totalFiles = folders.reduce((sum, f) => sum + f.file_count, 0);

  const selected = useMemo(() => {
    if (!folders.length) return null;
    const leaves = collectLeaves(folders);
    if (selectedSlug) {
      const hit = findNode(folders, selectedSlug);
      if (hit) return hit;
    }
    return leaves.find((l) => l.file_count > 0) ?? leaves[0] ?? null;
  }, [folders, selectedSlug]);

  if (isLoading) return <div className="flex justify-center py-10 mt-4"><Spinner /></div>;
  if (isError || !data) {
    return <div className="mt-4"><ErrorLine error={error ?? true} fallback="Nie udało się wczytać plików projektu." /></div>;
  }

  const submitNewFolder = () => {
    setCreateError("");
    createFolderM.mutate(newFolderLabel, {
      onSuccess: () => { setCreatingFolder(false); setNewFolderLabel(""); },
      onError: (e) => setCreateError(errMsg(e, "Nie udało się utworzyć folderu.")),
    });
  };
  const submitRenameFolder = () => {
    if (!renamingFolder) return;
    setRenameError("");
    renameFolderM.mutate(renamingFolder, {
      onSuccess: () => setRenamingFolder(null),
      onError: (e) => setRenameError(errMsg(e, "Nie udało się zmienić nazwy folderu.")),
    });
  };
  const onDeleteFolder = (node: LedFolderNode) => {
    const suffix = node.file_count > 0
      ? ` ${node.file_count === 1 ? "Plik trafi" : `Pliki (${node.file_count}) trafią`} do „Inne / bez folderu".`
      : "";
    if (!confirm(`Usunąć folder „${node.label}"?${suffix}`)) return;
    deleteFolderM.mutate(node.slug, {
      onSuccess: () => { if (selectedSlug === node.slug) setSelectedSlug("manual_inne"); },
      onError: (e) => alert(errMsg(e, "Nie udało się usunąć folderu.")),
    });
  };
  const onSelect = (slug: string) => { setSelectedSlug(slug); setAdding(false); };
  // Zapis w podglądzie „jak podwykonawca" jest wyłączony — pasek podglądu to zapowiada.
  const canWrite = !preview;
  // Folder własny DLC 01 założony po kopii: w zakładce powstanie po dokopiowaniu — do tego
  // czasu nie ma do czego wgrywać (wiersz LedFolder jeszcze nie istnieje).
  const selectedIsPending = !!selected?.pending_folder;
  const manageableHere = selected ? selected.entries.filter((e) => e.can_manage) : [];

  return (
    <div className="mt-4 space-y-4">
      {internal && data.snapshot && (
        <SnapshotBar leadershipId={leadershipId} snapshot={data.snapshot} canResync={!!data.can_resync && canWrite} pendingCount={data.pending_count ?? 0} pendingError={data.pending_error} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,340px)_1fr] gap-4">
        <aside className="card overflow-hidden self-start">
          <div className="py-2 px-3 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] flex items-center justify-between gap-2">
            <p className="section-label">Foldery</p>
            <div className="flex items-center gap-2">
              {data.can_create_folder && canWrite && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                  title="Utwórz nowy folder własny"
                  onClick={() => { setCreatingFolder((v) => !v); setCreateError(""); }}
                >
                  <FolderPlus size={14} />
                </button>
              )}
              {totalFiles > 0 && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-primary)] disabled:opacity-60 transition-colors"
                  title="Pobierz wszystkie pliki ze strukturą folderów jako ZIP"
                  disabled={zipM.isPending}
                  onClick={() => zipM.mutate(null)}
                >
                  {zipM.isPending && zipM.variables == null ? <Spinner size="sm" /> : <FolderArchive size={14} />}
                  ZIP
                </button>
              )}
            </div>
          </div>
          {creatingFolder && (
            <div className="p-2 border-b border-[var(--color-border-muted)] space-y-1.5">
              <input
                className="input-base text-xs w-full"
                placeholder="Nazwa nowego folderu"
                value={newFolderLabel}
                autoFocus
                maxLength={80}
                onChange={(e) => setNewFolderLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFolderLabel.trim()) submitNewFolder();
                  if (e.key === "Escape") setCreatingFolder(false);
                }}
              />
              {createError && <p className="text-[11px] text-[var(--color-primary)] font-mono">{createError}</p>}
              <div className="flex gap-1.5">
                <Button size="sm" disabled={!newFolderLabel.trim()} loading={createFolderM.isPending} onClick={submitNewFolder}>Utwórz</Button>
                <Button size="sm" variant="ghost" onClick={() => setCreatingFolder(false)}>Anuluj</Button>
              </div>
            </div>
          )}
          {renamingFolder && (
            <div className="p-2 border-b border-[var(--color-border-muted)] space-y-1.5">
              <input
                className="input-base text-xs w-full"
                placeholder="Nowa nazwa folderu"
                value={renamingFolder.label}
                autoFocus
                maxLength={80}
                onChange={(e) => setRenamingFolder({ ...renamingFolder, label: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && renamingFolder.label.trim()) submitRenameFolder();
                  if (e.key === "Escape") setRenamingFolder(null);
                }}
              />
              {renameError && <p className="text-[11px] text-[var(--color-primary)] font-mono">{renameError}</p>}
              <div className="flex gap-1.5">
                <Button size="sm" disabled={!renamingFolder.label.trim()} loading={renameFolderM.isPending} onClick={submitRenameFolder}>Zmień nazwę</Button>
                <Button size="sm" variant="ghost" onClick={() => setRenamingFolder(null)}>Anuluj</Button>
              </div>
            </div>
          )}
          <div className="py-1">
            {folders.map((n) => (
              <TreeNode
                key={n.slug}
                node={n}
                depth={0}
                selectedSlug={selected?.slug ?? ""}
                onSelect={onSelect}
                onDownloadZip={(slug) => zipM.mutate(slug)}
                zipBusy={zipM.isPending}
                canHide={canHide}
                onToggleHidden={(slug, hidden) =>
                  hideFolderM.mutate({ slug, hidden }, { onError: (e) => alert(errMsg(e, "Nie udało się zmienić widoczności folderu.")) })}
                hideBusy={hideFolderM.isPending}
                onRenameFolder={(node) => { setRenameError(""); setRenamingFolder({ slug: node.slug, label: node.label }); }}
                onDeleteFolder={onDeleteFolder}
              />
            ))}
          </div>
        </aside>

        <section className="space-y-4 min-w-0">
          {selected && (
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <h3 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-1.5">
                  {selected.label}
                  {selected.hidden_effective && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-normal text-amber-600" title={HIDDEN_HINT}>
                      <EyeOff size={11} /> ukryty dla podwykonawcy
                    </span>
                  )}
                  {(() => {
                    const n = selected.entries.filter((e) => e.pending_copy).length;
                    return n > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-normal text-amber-700 dark:text-amber-400" title="Pliki projektu ELEKTIM, których firma jeszcze nie widzi — skopiuje je „Dokopiuj nowe pliki projektu”">
                        <CloudOff size={11} /> {n === 1 ? "1 plik nieskopiowany" : `${n} nieskopiowanych`}
                      </span>
                    ) : null;
                  })()}
                </h3>
                {selected.source_hint && (
                  <p className="text-xs text-[var(--color-text-muted)] mt-0.5 flex items-start gap-1">
                    <Info size={12} className="mt-0.5 shrink-0" />
                    <span>{selected.source_hint}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 shrink-0 flex-wrap">
                {canWrite && selected.entries.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Kopiuj całą zawartość folderu do innego folderu"
                    onClick={() => setTransferTarget({ operation: "copy", source: { kind: "folder", slug: selected.slug, label: selected.label } })}
                  >
                    <Copy size={13} className="mr-1.5" />Kopiuj do…
                  </Button>
                )}
                {canWrite && manageableHere.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Przenieś pliki, którymi zarządzasz, do innego folderu"
                    onClick={() => setTransferTarget({ operation: "move", source: { kind: "folder", slug: selected.slug, label: selected.label } })}
                  >
                    <FolderInput size={13} className="mr-1.5" />Przenieś do…
                  </Button>
                )}
                {selected.file_count > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={zipM.isPending && zipM.variables === selected.slug}
                    disabled={zipM.isPending}
                    onClick={() => zipM.mutate(selected.slug)}
                  >
                    <Download size={13} className="mr-1.5" />Pobierz ZIP
                  </Button>
                )}
                {canWrite && !adding && !selectedIsPending && (
                  <Button size="sm" onClick={() => setAdding(true)}>
                    <Upload size={13} className="mr-1.5" />Dodaj plik
                  </Button>
                )}
              </div>
            </div>
          )}

          {canWrite && adding && selected && (
            <UploadForm leadershipId={leadershipId} folderSlug={selected.slug} onDone={() => setAdding(false)} />
          )}

          {selected && selected.entries.length === 0 && (
            <div className="card p-8 text-center">
              <Paperclip size={24} className="mx-auto mb-2 text-[var(--color-text-muted)]" />
              <p className="text-sm text-[var(--color-text-muted)]">Brak plików w tym folderze.</p>
            </div>
          )}

          {selected && selected.entries.length > 0 && (
            <div className="card overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                    <th className="py-2 pl-4 pr-2 w-6" />
                    <th className="py-2 pr-4 section-label">Nazwa / opis</th>
                    <th className="py-2 pr-4 section-label whitespace-nowrap">Rozmiar</th>
                    <th className="py-2 pr-4 section-label whitespace-nowrap">Data</th>
                    <th className="py-2 pr-3 w-16" />
                  </tr>
                </thead>
                <tbody>
                  {selected.entries.map((entry) => {
                    // Zdjęcia (jpg/png…) → podgląd jak w GG; reszta wg discovery Collabory.
                    const action = filesTabOpenAction(entry.filename, editorExt);
                    const fromTile = entry.tile_file_id != null;
                    // Załącznik kafla: zapis wg reguły kafla (ELEKTIM każdy, firma swoje), nie `can_manage`.
                    const writable = fromTile ? !!entry.tile_can_edit && !preview : entry.can_manage && canWrite;
                    const openAction: FilesTabOpenAction | undefined = action === "image"
                      ? (entry.url ? "image" : undefined)
                      : action
                        ? (action === "edit" && writable ? "edit" : "view")
                        : undefined;
                    const entriesHere = selected.entries;
                    return (
                    <EntryRow
                      key={entry.id}
                      entry={{ ...entry, can_manage: entry.can_manage && canWrite }}
                      contractorName={contractorName}
                      saving={patchM.isPending}
                      openAction={openAction}
                      onOpen={
                        openAction === "image"
                          ? () => setLightbox(imageSeries(entriesHere, entry))
                          : openAction ? () => setEditorFile(entry) : undefined
                      }
                      onSaveDescription={(description, close) =>
                        patchM.mutate({ id: entry.id, description }, { onSuccess: close, onError: (e) => alert(errMsg(e, "Nie udało się zapisać opisu.")) })}
                      onSaveFilename={(filename, close) =>
                        patchM.mutate({ id: entry.id, filename }, { onSuccess: close, onError: (e) => alert(errMsg(e, "Nie udało się zmienić nazwy.")) })}
                      onDelete={() => {
                        if (confirm(`Usunąć plik „${entry.filename}"?`)) {
                          removeM.mutate(entry.id, { onError: (e) => alert(errMsg(e, "Nie udało się usunąć pliku.")) });
                        }
                      }}
                      onToggleHidden={
                        canHide && !fromTile
                          ? (hidden) => hideFileM.mutate({ id: entry.id, hidden }, { onError: (e) => alert(errMsg(e, "Nie udało się zmienić widoczności pliku.")) })
                          : undefined
                      }
                      onPrehide={
                        (entry.pending_copy || (entry.title_document && !entry.hidden_from_contractor)) && canHide
                          ? () => prehideM.mutate(
                              { managed_file_id: entry.managed_file_id, folder_slug: selected.slug },
                              { onError: (e) => alert(errMsg(e, "Nie udało się ukryć pliku.")) },
                            )
                          : undefined
                      }
                      onCopy={() =>
                        setTransferTarget({ operation: "copy", source: { kind: "file", filename: entry.filename, fromSlug: selected.slug, fileId: entry.id } })}
                      onMove={
                        entry.can_manage && canWrite
                          ? () => setTransferTarget({ operation: "move", source: { kind: "file", filename: entry.filename, fromSlug: selected.slug, fileId: entry.id } })
                          : undefined
                      }
                    />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {transferTarget && (
        <TransferModal leadershipId={leadershipId} target={transferTarget} folders={folders} onClose={() => setTransferTarget(null)} />
      )}
      {lightbox && (
        <MediaLightbox
          items={lightbox.items}
          index={lightbox.index}
          onIndexChange={(index) => setLightbox({ ...lightbox, index })}
          onClose={() => setLightbox(null)}
        />
      )}
      {editorFile && editorFile.pending_copy && (
        // Plik jeszcze nieskopiowany żyje w DLC 01 — otwieramy go slotem zakładki projektu
        // (wiersz ręczny po jego id, wpis z projekcji parą „<connection>:<plik>").
        <OdtEditorScreen
          kind="project_file"
          id={editorFile.source_ref?.kind === "connection_file" ? editorFile.source_ref.id : `${data.connection_id}:${editorFile.managed_file_id}`}
          readOnly={preview || editorFile.source_ref?.kind !== "connection_file"}
          onClose={() => setEditorFile(null)}
        />
      )}
      {editorFile && !editorFile.pending_copy && editorFile.tile_file_id != null && (
        // Załącznik kafla („Etapy") — własny slot; zapis wg reguły kafla (tile_can_edit).
        <OdtEditorScreen
          kind="led_tile_file"
          id={String(editorFile.tile_file_id)}
          readOnly={preview || !editorFile.tile_can_edit}
          onClose={(saved) => {
            setEditorFile(null);
            if (saved) {
              qc.invalidateQueries({ queryKey: filesKey(leadershipId) });
              // Kafel pokazuje ten sam wiersz — „Etapy" też mają zobaczyć nowy rozmiar/plik.
              qc.invalidateQueries({ queryKey: ["contractor-led", "tiles", leadershipId] });
            }
          }}
        />
      )}
      {editorFile?.title_document && (
        // Dokument tytułu — zawsze do podglądu: to dowód, na którym stoi tytuł.
        <OdtEditorScreen kind="led_title_file" id={editorFile.managed_file_id} readOnly onClose={() => setEditorFile(null)} />
      )}
      {editorFile && !editorFile.pending_copy && editorFile.tile_file_id == null && !editorFile.title_document && (
        <OdtEditorScreen
          kind="led_file"
          id={editorFile.id}
          // Podgląd „jak podwykonawca" i pliki, którymi widz nie zarządza — bez prawa zapisu
          // (backend i tak go nie da; tu tylko nie udajemy trybu edycji).
          readOnly={preview || !editorFile.can_manage}
          onClose={(saved) => {
            setEditorFile(null);
            // Zapis zmienia plik (rozmiar, czas) — drzewo trzeba odświeżyć.
            if (saved) qc.invalidateQueries({ queryKey: filesKey(leadershipId) });
          }}
        />
      )}
    </div>
  );
}
